import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UPButton from 'components/button';
import { UPTanStackTableColumn } from 'components/tanstack-table';
import { CompanyRole } from 'constants/CompanyRole';
import { TFunction } from 'i18next';
import { OverrulableDot, PermissionText, UnitName } from 'modules/management/time-registration-policy/styles';
import { UnitDefinition } from 'modules/management/time-registration-policy/units';
import {
  TIME_REGISTRATION_POLICY__COLUMN_SETTING,
  TIME_REGISTRATION_POLICY__EDIT,
  TIME_REGISTRATION_POLICY__LOCKED,
  TIME_REGISTRATION_POLICY__OVERRULABLE,
  TIME_REGISTRATION_SETTINGS__COLUMN_STATE,
  TIME_REGISTRATION_SETTINGS__COLUMN_VALUE,
  TIME_REGISTRATION_SETTINGS__OVERRULED,
  TIME_REGISTRATION_SETTINGS__VIEW,
} from 'translations/constants';
import { CompanyUnitState, CompanyUnitStatus } from 'types/company/time-registration/ICompanyTimeRegistrationPolicy';
import { PolicyUnit } from 'types/time-registration-policy/ITimeRegistrationPolicy';
import { canEditUnit, formatCompanyUnitValue, getUnitStatus } from './helpers';

export interface CompanyPolicyRow {
  def: UnitDefinition;
  state: CompanyUnitState;
}

export const getCompanyPolicyRowName = (row: CompanyPolicyRow): string => PolicyUnit[row.def.unit];

const STATUS_LABEL_KEYS: Record<CompanyUnitStatus, string> = {
  overrulable: TIME_REGISTRATION_POLICY__OVERRULABLE,
  overruled: TIME_REGISTRATION_SETTINGS__OVERRULED,
  locked: TIME_REGISTRATION_POLICY__LOCKED,
};

export const renderUnitStatus = (status: CompanyUnitStatus, t: TFunction): JSX.Element => (
  <PermissionText>
    {status === 'overrulable' && <OverrulableDot />}
    {status === 'overruled' && <EditOutlinedIcon sx={{ fontSize: 12 }} />}
    {status === 'locked' && <LockOutlinedIcon sx={{ fontSize: 12 }} />}
    {t(STATUS_LABEL_KEYS[status])}
  </PermissionText>
);

export const buildCompanyPolicyColumns = (
  t: TFunction,
  role: CompanyRole,
  onOpen: (unit: PolicyUnit) => void,
): UPTanStackTableColumn<CompanyPolicyRow>[] => [
  {
    id: 'setting',
    header: t(TIME_REGISTRATION_POLICY__COLUMN_SETTING),
    cell: ({ row }) => <UnitName title={t(row.def.nameKey)}>{t(row.def.nameKey)}</UnitName>,
  },
  {
    id: 'value',
    header: t(TIME_REGISTRATION_SETTINGS__COLUMN_VALUE),
    cell: ({ row }) => (
      <span data-testid={`tr-company-value-${getCompanyPolicyRowName(row)}`}>
        {formatCompanyUnitValue(row.def, row.state, t)}
      </span>
    ),
  },
  {
    id: 'state',
    header: t(TIME_REGISTRATION_SETTINGS__COLUMN_STATE),
    cell: ({ row }) => (
      <span data-testid={`tr-company-state-${getCompanyPolicyRowName(row)}`}>
        {renderUnitStatus(getUnitStatus(row.state), t)}
      </span>
    ),
  },
  {
    id: 'actions',
    header: '',
    align: 'right',
    cell: ({ row }) => {
      const editable = canEditUnit(row.state, role);
      return (
        <UPButton
          text={t(editable ? TIME_REGISTRATION_POLICY__EDIT : TIME_REGISTRATION_SETTINGS__VIEW)}
          outlined
          onClick={() => onOpen(row.def.unit)}
          testId={`tr-company-${editable ? 'edit' : 'view'}-${getCompanyPolicyRowName(row)}`}
        />
      );
    },
  },
];
